import React, { useState } from 'react';
import { Loader2, Mail, Lock, User, Eye, EyeOff, ArrowRight, Shield } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { onboardingApi, signInNewAccount } from '../lib/onboarding';
import logoDark from '../../assets/logo-dark.png';
import logoLight from '../../assets/logo-light.png';

export function SignupPage() {
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const [form, setForm] = useState({ firstName: '', email: '', password: '', confirm: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('A palavra-passe deve ter pelo menos 8 caracteres.');
      return;
    }
    if (form.password !== form.confirm) {
      toast.error('As palavras-passe não coincidem.');
      return;
    }
    setBusy(true);
    try {
      await onboardingApi.registerAccount({ email: form.email, password: form.password, firstName: form.firstName });
      await signInNewAccount(form.email, form.password);
      localStorage.setItem('app_logged_in', 'true');
      localStorage.setItem('app_username', form.email);
      localStorage.setItem('app_user_role', 'manager');
      localStorage.setItem('is_master_admin', 'false');
      localStorage.setItem('last_login_timestamp', Date.now().toString());
      toast.success('Conta criada com sucesso!');
      window.location.href = '/onboarding';
    } catch (err: any) {
      toast.error(err?.message || 'Não foi possível criar a conta.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50 dark:bg-black relative overflow-hidden">
      <div className="absolute inset-0 bg-brand/10 blur-[120px] pointer-events-none rounded-full w-[50vw] h-[50vw] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      <Card className="w-full max-w-[420px] shadow-2xl border border-slate-200 dark:border-slate-800/60 bg-white/90 dark:bg-[#111111]/90 backdrop-blur-xl rounded-2xl relative z-10">
        <CardHeader className="text-center space-y-4 pt-8 pb-4">
          <div className="mx-auto flex justify-center">
            <img src={isDark ? logoLight : logoDark} alt="LojaPilot" className="w-48 h-auto object-contain" />
          </div>
          <p className="text-[11px] font-medium tracking-widest text-slate-500 dark:text-slate-400 uppercase">
            Criar a sua conta
          </p>
        </CardHeader>
        <form onSubmit={submit} className="w-full px-8">
          <CardContent className="space-y-5 px-0 pb-6">
            <div className="space-y-2">
              <Label htmlFor="su-name" className="text-[10px] font-bold tracking-wider text-slate-600 dark:text-slate-500 uppercase">Nome</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="su-name" className="h-11 pl-10 bg-white dark:bg-[#0a0a0a] border-slate-200 dark:border-slate-800/80 rounded-lg text-sm" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="O seu nome" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="su-email" className="text-[10px] font-bold tracking-wider text-slate-600 dark:text-slate-500 uppercase">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="su-email" type="email" required className="h-11 pl-10 bg-white dark:bg-[#0a0a0a] border-slate-200 dark:border-slate-800/80 rounded-lg text-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@exemplo.com" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="su-pass" className="text-[10px] font-bold tracking-wider text-slate-600 dark:text-slate-500 uppercase">Palavra-passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="su-pass" type={showPassword ? 'text' : 'password'} required minLength={8} className="h-11 pl-10 pr-10 bg-white dark:bg-[#0a0a0a] border-slate-200 dark:border-slate-800/80 rounded-lg text-sm" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mínimo 8 caracteres" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="su-confirm" className="text-[10px] font-bold tracking-wider text-slate-600 dark:text-slate-500 uppercase">Confirmar palavra-passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="su-confirm" type={showPassword ? 'text' : 'password'} required className="h-11 pl-10 bg-white dark:bg-[#0a0a0a] border-slate-200 dark:border-slate-800/80 rounded-lg text-sm" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} placeholder="••••••••" />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 text-sm font-semibold rounded-lg border-none text-white flex items-center justify-center gap-2" style={{ backgroundColor: '#10B981', color: '#03291F' }}>
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> A criar conta...</> : <>Criar conta <ArrowRight className="w-4 h-4" /></>}
            </Button>
            <p className="text-center text-xs text-slate-500">
              Já tem conta? <a href="/login" className="font-semibold text-emerald-600 hover:underline">Iniciar sessão</a>
            </p>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-slate-500">
              <Shield className="w-3 h-3" /> Os seus dados ficam protegidos no servidor LojaPilot.
            </div>
          </CardContent>
        </form>
      </Card>
      <Toaster richColors position="top-right" theme={isDark ? 'dark' : 'light'} />
    </div>
  );
}
